import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Package, CalendarDays, Search, Printer, RefreshCw,
} from "lucide-react";
import InventoryPrintReport from "@/components/InventoryPrintReport";
import ReconciliationPrintReport from "@/components/ReconciliationPrintReport";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

function fmtKg(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

export default function Inventory() {
  const { authAxios, logout, username, isViewer } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [allMaterials, setAllMaterials] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [lotsSummary, setLotsSummary] = useState({});
  const [prevMonthData, setPrevMonthData] = useState({ consumption: {}, label: "" });
  const [mode, setMode] = useState("stoc"); // "stoc" | "reconciliere"
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [showReconPrint, setShowReconPrint] = useState(false);

  const loadReconciliation = async () => {
    setReconLoading(true);
    try {
      const res = await authAxios.get(`/inventory/reconciliation/${year}/${month}`);
      setReconData(res.data);
    } catch {
      toast.error("Eroare la încărcare reconciliere");
    } finally {
      setReconLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "reconciliere") loadReconciliation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, year, month]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 2; y++) arr.push(y);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      // Previous month
      let prevYear = year, prevMonth = month - 1;
      if (prevMonth < 1) { prevMonth = 12; prevYear = year - 1; }

      const [matsRes, invRes, lotsRes, prevAggRes, prevInvRes] = await Promise.all([
        authAxios.get("/materials"),
        authAxios.get(`/inventory/${year}/${month}`),
        authAxios.get("/lots"),
        authAxios.get(`/reports/monthly/${prevYear}/${prevMonth}`).catch(() => ({ data: { materials: [] } })),
        authAxios.get(`/inventory/${prevYear}/${prevMonth}`).catch(() => ({ data: null })),
      ]);
      setAllMaterials(matsRes.data.materials || []);
      const map = {};
      if (invRes.data && invRes.data.materials) {
        for (const m of invRes.data.materials) {
          map[m.name] = String(m.initial_stock);
        }
      }
      setStockMap(map);
      const summary = {};
      for (const lot of lotsRes.data || []) {
        const k = lot.material_name;
        if (!summary[k]) summary[k] = { remaining: 0, original: 0, consumed: 0, lots_count: 0, active_lot: null, active_remaining: 0 };
        summary[k].remaining += lot.remaining_qty || 0;
        summary[k].original += lot.original_qty || 0;
        summary[k].consumed += lot.consumed_qty || 0;
        summary[k].lots_count += 1;
        if (!summary[k].active_lot && lot.remaining_qty > 0.0001) {
          summary[k].active_lot = lot.lot_number;
          summary[k].active_remaining = lot.remaining_qty;
        }
      }
      setLotsSummary(summary);

      // Previous month consumption & initial stock per material
      const consumption = {};
      const prevInitial = {};
      for (const mat of prevAggRes.data?.materials || []) {
        consumption[mat.name] = mat.consumption || 0;
      }
      for (const mat of prevInvRes.data?.materials || []) {
        prevInitial[mat.name] = mat.initial_stock || 0;
      }
      setPrevMonthData({
        consumption,
        prevInitial,
        label: `${MONTHS_RO[prevMonth - 1]} ${prevYear}`,
        year: prevYear,
        month: prevMonth,
      });
    } catch (err) {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const onChange = (name, val) => {
    setStockMap((prev) => ({ ...prev, [name]: val }));
  };

  const onCopyPreviousMonth = async () => {
    let prevYear = year, prevMonth = month - 1;
    if (prevMonth < 1) { prevMonth = 12; prevYear = year - 1; }
    try {
      const res = await authAxios.get(`/inventory/${prevYear}/${prevMonth}`);
      if (res.data && res.data.materials && res.data.materials.length > 0) {
        const map = {};
        for (const m of res.data.materials) map[m.name] = String(m.initial_stock);
        setStockMap(map);
        toast.success(`Inventar copiat din ${MONTHS_RO[prevMonth - 1]} ${prevYear}`);
      } else {
        toast.warning("Nu există inventar pentru luna precedentă");
      }
    } catch {
      toast.error("Eroare la copiere");
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const materials = allMaterials.map((name) => ({
        name,
        initial_stock: parseFloat(stockMap[name]) || 0,
      }));
      await authAxios.post("/inventory", { year, month, materials });
      toast.success(`Inventar salvat: ${MONTHS_RO[month - 1]} ${year}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allMaterials;
    return allMaterials.filter((m) => m.toLowerCase().includes(s));
  }, [allMaterials, search]);

  const totalFilled = Object.values(stockMap).filter((v) => parseFloat(v) > 0).length;
  const totalStock = Object.values(stockMap).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="back-to-dashboard"
              className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-amber-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi la producție
            </Link>
            <div className="h-5 w-px bg-neutral-200" />
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-600" />
              <h1 className="text-lg font-bold text-neutral-900">Inventar lunar</h1>
            </div>
            <nav className="hidden md:flex items-center gap-1 ml-4 border-l border-neutral-200 pl-4">
              <Link to="/" data-testid="nav-dashboard" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Producție Zi</Link>
              <Link to="/inventar" data-testid="nav-inventar" className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">Inventar</Link>
              <Link to="/rapoarte" data-testid="nav-rapoarte" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Rapoarte</Link>
              <Link to="/calculator" data-testid="nav-calculator" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Calculator</Link>
            </nav>
          </div>
          <button
            data-testid="inventory-logout"
            onClick={logout}
            className="text-xs text-neutral-500 hover:text-neutral-900"
          >
            {username} · Deconectare
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 pr-3 border-r border-neutral-200">
              <CalendarDays className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">Perioada</span>
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">An</label>
              <select
                data-testid="inv-year-select"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">Lună</label>
              <select
                data-testid="inv-month-select"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              >
                {MONTHS_RO.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div className="flex-1" />
            <button
              data-testid="print-inventory-button"
              onClick={() => setShowPrint(true)}
              className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-white hover:bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              Tipărește inventar
            </button>
            {!isViewer && (
              <>
                <button
                  data-testid="copy-prev-month-button"
                  onClick={onCopyPreviousMonth}
                  className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Copiază din luna precedentă
                </button>
                <button
                  data-testid="save-inventory-button"
                  onClick={onSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "Se salvează..." : "Salvează inventar"}
                </button>
              </>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center gap-6 text-xs text-neutral-500">
            <span>Materii prime: <strong className="text-neutral-900">{allMaterials.length}</strong></span>
            <span>Completate: <strong className="text-amber-700">{totalFilled}</strong></span>
            <span>Total stoc: <strong className="text-emerald-700 tabular-nums">{totalStock.toFixed(3)} kg</strong></span>
          </div>
        </div>

        <div className="mb-4 inline-flex rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
          <button
            data-testid="mode-stoc-btn"
            onClick={() => setMode("stoc")}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "stoc" ? "bg-amber-600 text-white shadow-sm" : "text-neutral-600 hover:bg-neutral-100"}`}
          >
            Stoc inițial
          </button>
          <button
            data-testid="mode-reconciliere-btn"
            onClick={() => {
              // La deschidere, sar automat pe luna trecută (ultima încheiată)
              const now = new Date();
              let y = now.getFullYear();
              let m = now.getMonth(); // 0-11 = luna curentă - 1 (adică luna trecută în 1-12)
              if (m < 1) { m = 12; y -= 1; }
              setYear(y);
              setMonth(m);
              setMode("reconciliere");
            }}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "reconciliere" ? "bg-amber-600 text-white shadow-sm" : "text-neutral-600 hover:bg-neutral-100"}`}
          >
            🔁 Reconciliere lunară
          </button>
        </div>

        {mode === "stoc" && (
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 bg-neutral-50/50 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-neutral-900">
                Stoc inițial · {MONTHS_RO[month - 1]} {year}
              </h3>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                Introdu cantitatea în kg pe care o ai pe stoc la începutul lunii pentru fiecare materie primă.
              </p>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="inv-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută materie primă..."
                className="rounded-md border border-neutral-200 bg-white pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 w-60"
              />
            </div>
          </div>
          {loading && (
            <div className="p-12 text-center text-sm text-neutral-400">Se încarcă...</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-12 text-center text-sm text-neutral-400">
              Nicio materie primă găsită.
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold tracking-wider uppercase text-neutral-500 bg-neutral-50">
                  <th className="px-5 py-2.5 w-10">#</th>
                  <th className="px-5 py-2.5">Materie primă</th>
                  <th className="px-5 py-2.5 w-40 text-right">Stoc inițial (kg)</th>
                </tr>
              </thead>
              <tbody data-testid="inventory-list">
                {filtered.map((m, idx) => {
                  const val = stockMap[m] || "";
                  return (
                    <tr key={m} className="border-t border-neutral-100">
                      <td className="px-5 py-1.5 text-neutral-400 text-xs tabular-nums">{idx + 1}</td>
                      <td className="px-5 py-1.5 text-neutral-900 capitalize">{m}</td>
                      <td className="px-5 py-1.5 text-right">
                        {isViewer ? (
                          <span className="inline-block w-32 text-right text-sm tabular-nums font-semibold text-neutral-700">
                            {val ? parseFloat(val).toFixed(3) : "0"}
                          </span>
                        ) : (
                          <input
                            data-testid={`inv-input-${m.replace(/\s/g, "-")}`}
                            type="number"
                            step="any"
                            min="0"
                            value={val}
                            onChange={(e) => onChange(m, e.target.value)}
                            placeholder="0"
                            className={`w-32 text-right rounded-md border px-2.5 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${val && parseFloat(val) > 0 ? "border-emerald-500 bg-emerald-50 font-semibold text-emerald-900" : "border-neutral-200 bg-white text-neutral-700"}`}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )}

        {mode === "reconciliere" && (
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden" data-testid="reconciliation-section">
          <div className="px-5 py-3 border-b border-neutral-200 bg-neutral-50/50 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-sm font-bold text-neutral-900">
                Reconciliere lună {MONTHS_RO[month - 1]} {year}
              </h3>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                Consum din rapoartele zilnice · Intrări din loturi · Inventar faptic = stocul introdus pentru {MONTHS_RO[month % 12]} {month === 12 ? year + 1 : year} · Diferență = faptic − teoretic (− = lipsă)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="refresh-reconciliation-button"
                onClick={loadReconciliation}
                disabled={reconLoading}
                title="Recalculează la zi din loturi și rețete"
                className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white hover:bg-neutral-50 disabled:opacity-40 px-3 py-2 text-xs font-semibold text-neutral-700 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${reconLoading ? "animate-spin" : ""}`} />
                Reîmprospătează
              </button>
              <button
                data-testid="print-reconciliation-button"
                onClick={() => setShowReconPrint(true)}
                disabled={!reconData}
                className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-white hover:bg-amber-50 disabled:opacity-40 px-3 py-2 text-xs font-semibold text-amber-700 transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
                Tipărește reconciliere
              </button>
            </div>
          </div>

          {!reconData?.has_faptic && (
            <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800">
              Coloana „Inventar faptic" apare după ce salvezi „Stoc inițial" pentru luna următoare ({MONTHS_RO[month % 12]} {month === 12 ? year + 1 : year}) — adică inventarul fizic de la finalul lunii {MONTHS_RO[month - 1]}.
            </div>
          )}

          {reconLoading ? (
            <div className="p-12 text-center text-sm text-neutral-400">Se încarcă...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 bg-neutral-50">
                    <th className="px-3 py-2.5 w-8 text-left">#</th>
                    <th className="px-3 py-2.5 text-left">Materie primă</th>
                    <th className="px-3 py-2.5 text-right">Stoc inițial</th>
                    <th className="px-3 py-2.5 text-right text-emerald-600">+ Intrări</th>
                    <th className="px-3 py-2.5 text-right">= Total</th>
                    <th className="px-3 py-2.5 text-right text-amber-600">− Consum</th>
                    <th className="px-3 py-2.5 text-right">Stoc final teoretic</th>
                    <th className="px-3 py-2.5 text-right">Inventar faptic</th>
                    <th className="px-3 py-2.5 text-right">Diferență</th>
                  </tr>
                </thead>
                <tbody data-testid="reconciliation-list">
                  {(reconData?.rows || [])
                    .filter((r) => r.initial || r.entries || r.consumption || r.has_physical)
                    .map((r, idx) => (
                      <tr key={r.name} className="border-t border-neutral-100" data-testid={`recon-row-${r.name.replace(/\s/g, "-")}`}>
                        <td className="px-3 py-1.5 text-neutral-400 tabular-nums">{idx + 1}</td>
                        <td className="px-3 py-1.5 text-neutral-900 capitalize">{r.name}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{fmtKg(r.initial)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{fmtKg(r.entries)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtKg(r.total)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-amber-700">{fmtKg(r.consumption)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtKg(r.theoretical_final)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{r.has_physical ? fmtKg(r.physical_final) : "—"}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
                          r.diff === null ? "text-neutral-300" :
                          Math.abs(r.diff) < 0.001 ? "text-neutral-500" :
                          r.diff > 0 ? "text-emerald-700" : "text-red-700"
                        }`}>
                          {r.diff === null ? "—" : (r.diff > 0 ? `+${fmtKg(r.diff)}` : fmtKg(r.diff))}
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-bold">
                    <td colSpan={2} className="px-3 py-2 text-right uppercase tracking-wider text-[10px]">Total (kg)</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtKg(reconData?.totals?.initial)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtKg(reconData?.totals?.entries)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtKg(reconData?.totals?.total)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtKg(reconData?.totals?.consumption)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtKg(reconData?.totals?.theoretical_final)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtKg(reconData?.totals?.physical_final)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className="text-emerald-700">+{fmtKg(reconData?.totals?.diff_plus)}</span>
                      {" / "}
                      <span className="text-red-700">{fmtKg(reconData?.totals?.diff_minus)}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
        )}
      </div>

      {showPrint && (
        <InventoryPrintReport
          materials={allMaterials}
          stockMap={stockMap}
          year={year}
          month={month}
          lotsSummary={lotsSummary}
          prevMonth={prevMonthData}
          onClose={() => setShowPrint(false)}
        />
      )}

      {showReconPrint && reconData && (
        <ReconciliationPrintReport
          data={reconData}
          year={year}
          month={month}
          onClose={() => setShowReconPrint(false)}
        />
      )}
    </div>
  );
}
